import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CreditScoreCard,
  DataGridCard,
  DivAuthorizedWrapper,
  GridEmptyCard,
  ManualLoader,
  Messages,
  NotificationMessage,
  ScoreProgressChartCard,
  Wrapper,
} from "src/components";
import { ContentContainer, PageHeader } from "src/components/Layouts/AppShell";
import {
  set_old_date_for_rent_reporting_history,
  sortingArrayByDateForRentStatement,
} from "src/helpers/helperRent";
import {
  creditScroreBgColor,
  creditScroreIntervals,
} from "src/shared/assets/data";
import { theme } from "src/shared/theme/theme";
import { todayDateYYYYMMDD } from "src/shared/utilities/dateConvertions";
import {
  useGetCreditScoreQuery,
  useGetReportingStatementsQuery,
  useGetUserQuery,
  useSubscribeRentReportMutation,
} from "src/store/services/privateApi";
import { ViewMyCreditScoreModal } from "src/components/Modals/ViewMyCreditScoreModal";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { OpOut } from "./OptOut";
import { IdentityVerification } from "../../Account/Reporting/Identity";
import { getFullName } from "../../../helpers/helperSettings";
import { CredBuildBanner } from "./CredBuildBanner";
import CreditReportingAcknowledgement from "./CredBuildAcknowledgement";

export const RentReportingHomeScreenContainer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [gridData, setGridData] = useState<any[]>([]);
  const [kyc, setKyc] = useState(false);
  const [idVerificationDone, setIdVerificationDone] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>("");

  const [viewOptOut, setViewOptOut] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  // get the user api details ___
  const {
    data: userData,
    isSuccess: isLoadingUserDataSuccess,
    isLoading: isLoadingUserData,
  } = useGetUserQuery(null);

  const [trigger, { isLoading: isLoadingSubscribe }] =
    useSubscribeRentReportMutation();
  // get the transaction list via api ___
  const {
    data: rentReportingData,
    isSuccess: isSuccessRentReportingData,
    isLoading: isLoadingRentReportingData,
  }: any = useGetReportingStatementsQuery({
    startDate: set_old_date_for_rent_reporting_history,
    endDate: todayDateYYYYMMDD(),
  });

  // get credit score
  const {
    data: creditScoreData,
    isSuccess: isSuccessCreditScoreData,
    isLoading: isLoadingCreditScoreData,
  } = useGetCreditScoreQuery(null);
  useEffect(() => {
    if (isLoadingUserDataSuccess) {
      setIdVerificationDone(
        userData?.user?.person?.identityVerificationSessions?.some(
          (s: any) => s.status === "SUCCESS"
        )
      );
    }
  }, [userData]);
  useEffect(() => {
    console.log("Error message changed:", errorMessage);
  }, [errorMessage]);

  useEffect(() => {
    if (
      isSuccessRentReportingData &&
      rentReportingData &&
      rentReportingData.length > 0
    ) {
      const sortedArray = sortingArrayByDateForRentStatement(
        rentReportingData,
        true
      );
      setGridData(sortedArray);
    }
  }, [isSuccessRentReportingData, rentReportingData]);

  const property = userData?.user?.currentLease.property;

  return (
    <>
      {(isLoadingUserData || isLoadingSubscribe) && <ManualLoader />}
      <Wrapper isAuthorizedWrapper idSelector={"wrapperContainer"}>
        <ContentContainer size="default">
          <PageHeader
            title={t("rentReporting.header")}
            onBack={() => navigate("/rent")}
          />
          <DivAuthorizedWrapper
            sx={{
              [`@media(min-width: ${theme.breakpoints.values.sm}px)`]: {
                marginBottom: 4,
              },
              [`@media(max-width: ${theme.breakpoints.values.sm}px)`]: {
                paddingBottom: 0,
              },
            }}
          >
            {kyc && (
              <Box>
                {errorMessage && (
                  <Messages
                    messageHeading={"Identity Verification"}
                    messageContent={errorMessage}
                    closeEvent={() => setErrorMessage("")}
                    bottomMargin={4}
                    topMargin={4}
                  />
                )}
                <IdentityVerification
                  phoneNumber={userData?.user?.phone}
                  email={userData?.user?.email}
                  fullName={getFullName(
                    userData?.user?.person?.FirstName,
                    userData?.user?.person?.LastName
                  )}
                  personId={userData?.user?.person?.id}
                  onSubmitEvent={async (status, failureReason) => {
                    if (status === "success") {
                      setKyc(false);
                      setIdVerificationDone(true);
                    } else {
                      const reasonMsg = failureReason
                        ? t(
                            `account.plaidIdentityVerification.failure.${failureReason.code}`,
                            { defaultValue: "Identity Verification Failed" }
                          )
                        : "Identity Verification Failed";
                      setErrorMessage(reasonMsg);
                    }
                  }}
                />
              </Box>
            )}
            {isLoadingUserDataSuccess && !kyc && (
              <Box sx={{ backgroundColor: "background.paper" }}>
                {!idVerificationDone && (
                  <Box>
                    <CredBuildBanner></CredBuildBanner>
                    <NotificationMessage
                      messageHeading={t("rentReporting.header")}
                      messageContent={t("rentReporting.notReadyMessageContent")}
                      messageLinkEvent={() => setKyc(true)}
                      variant="rent_reporting_disabled"
                      topMargin={0}
                    />
                  </Box>
                )}
                {!userData?.user?.isCreditReportingReady &&
                  idVerificationDone && (
                    <Box>
                      {errorMessage && (
                        <Messages
                          messageHeading={"Identity Verification"}
                          messageContent={errorMessage}
                          closeEvent={() => setErrorMessage("")}
                          bottomMargin={4}
                          topMargin={4}
                        />
                      )}
                      <CreditReportingAcknowledgement
                        residentName={`${userData?.user?.person?.FirstName} ${userData?.user?.person?.LastName}`}
                        unitNumber={userData?.user?.currentLease.Unit}
                        property={property}
                      />

                      <Box sx={{ mt: 3 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={agreementAccepted}
                              onChange={(e) =>
                                setAgreementAccepted(e.target.checked)
                              }
                              sx={{ alignSelf: "flex-start", pt: 0 }}
                            />
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                          label={
                            <Typography variant="h1" color="gray.900">
                              I have read and agree to the Credit Reporting
                              Program Agreement above, and I consent to
                              CredBuild displaying my credit score. CredBuild
                              will never perform a credit check. A copy of this
                              agreement will be emailed to me.
                            </Typography>
                          }
                        />
                      </Box>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        sx={{ mt: 3 }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => navigate("/rent")}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          disabled={!agreementAccepted || isLoadingSubscribe}
                          onClick={async () => {
                            const res: any = await trigger({});
                            if (res.error) {
                              setErrorMessage(res.error.data?.err);
                            } else {
                              window.location.reload();
                            }
                          }}
                        >
                          Submit
                        </Button>
                      </Stack>
                    </Box>
                  )}

                {userData?.user?.isCreditReportingReady && (
                  <Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: { xs: 0, md: 3 },
                        alignItems: "start",
                      }}
                    >
                      <CreditScoreCard
                        creditScroreBgColor={creditScroreBgColor}
                        creditScroreIntervals={creditScroreIntervals}
                        isLoadingFadeEnabled={isLoadingCreditScoreData}
                        isSuccessLoadData={isSuccessCreditScoreData}
                        statmentData={creditScoreData}
                      />
                      <ScoreProgressChartCard
                        isSuccessLoadData={isSuccessCreditScoreData}
                        creditScoreData={creditScoreData}
                        isLoadingFadeEnabled={isLoadingCreditScoreData}
                      />
                    </Box>

                    {
                      /**
                       * display DataGridCard component with transaction data
                       * If transaction data length is more than 0 &&  api data loading is done
                       */
                      isSuccessRentReportingData &&
                        rentReportingData &&
                        rentReportingData?.length > 0 &&
                        !isLoadingRentReportingData && (
                          <DataGridCard
                            t={t}
                            dataObject={gridData}
                            dataGridCardTitle={t(
                              "rentReporting.reportingHistory.title"
                            )}
                            isRecentWorkOrders
                            isCardLinkAvailable
                            navigate={navigate}
                            isTooltipInfoEnabled
                            tooltipContent={t(
                              "rentReporting.reportingHistory.tooltipContent"
                            )}
                            gridType="reporting_history"
                            viewAllLink="/rent/reporting/list"
                            routeState={{
                              backRouteUrl: "/rent/reporting/home",
                            }}
                            iconArrowVisible={false}
                            isHandleArrowClickable={false}
                          />
                        )
                    }

                    {
                      /**
                       * display GridEmptyCard component for empty transaction data with empty state content
                       * this component will display after api data loading is done
                       */
                      isSuccessRentReportingData &&
                        rentReportingData &&
                        rentReportingData?.length === 0 &&
                        !isLoadingRentReportingData && (
                          <GridEmptyCard
                            title={t("rentReporting.reportingHistory.title")}
                            type="reporting_history"
                            isTooltipInfoEnabled
                            tooltipContent={t(
                              "rentReporting.reportingHistory.tooltipContent"
                            )}
                            emptyStateContent={t(
                              "rentReporting.reportingHistory.emptyStateContent"
                            )}
                            isButtonVisible={false}
                          />
                        )
                    }

                    {
                      /**
                       * display GridEmptyCard component without empty state content
                       * this component will display while api data loading
                       */
                      isLoadingRentReportingData &&
                        !isSuccessRentReportingData && (
                          <GridEmptyCard
                            title={t("rentReporting.reportingHistory.title")}
                            type="reporting_history"
                            isTooltipInfoEnabled={true}
                            tooltipContent={t(
                              "rentReporting.reportingHistory.listTooltipContent"
                            )}
                            isEmptyCardDataVisble={false}
                            isLoadingFadeEnabled={isLoadingUserData}
                            isInnerLoaderEnabled={
                              !isLoadingUserData && isLoadingRentReportingData
                            }
                          />
                        )
                    }
                  </Box>
                )}
              </Box>
            )}
          </DivAuthorizedWrapper>
          {userData?.user?.isCreditReportingReady && (
            <Link
              component="button"
              onClick={() => {
                setViewOptOut(true);
              }}
            >
              Opt Out of Credbuild
            </Link>
          )}
          <br></br>
        </ContentContainer>
      </Wrapper>
      {viewOptOut && (
        <OpOut
          handleCloseEvent={() => {
            setViewOptOut(false);
          }}
        />
      )}
    </>
  );
};
